import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./HorarioDespacho.css";

//const API_URL =window.path_service_website+"/api/horarios-despacho";

const DIAS = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

function generarIdTemporal() {
  if (
    typeof window !== "undefined" &&
    window.crypto?.randomUUID
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

function crearRangoVacio() {
  return {
    id: generarIdTemporal(),
    desde: "",
    hasta: "",
    costo: "0.00",
    cupos: "0",
  };
}

function crearHorariosVacios() {
  return DIAS.reduce((resultado, dia) => {
    resultado[dia] = [crearRangoVacio()];
    return resultado;
  }, {});
}

function crearFormularioVacio() {
  return {
    id: null,
    nombre: "",
    tipo: "todos",
    fecha: "",
    activo: true,
    horarios: crearHorariosVacios(),
  };
}

function obtenerFechaActual() {
  const fecha = new Date();

  const year = fecha.getFullYear();
  const month = String(
    fecha.getMonth() + 1
  ).padStart(2, "0");
  const day = String(fecha.getDate()).padStart(
    2,
    "0"
  );

  return `${year}-${month}-${day}`;
}

function convertirHoraAMinutos(hora) {
  if (!hora) {
    return 0;
  }

  const [horas, minutos] = hora
    .split(":")
    .map(Number);

  return horas * 60 + minutos;
}

function convertirHora12(hora) {
  if (!hora) {
    return "";
  }

  const [horaTexto, minutosTexto] =
    hora.split(":");

  const horaNumero = Number(horaTexto);
  const periodo =
    horaNumero >= 12 ? "pm" : "am";
  const hora12 = horaNumero % 12 || 12;

  if (minutosTexto === "00") {
    return `${hora12}${periodo}`;
  }

  return `${hora12}-${minutosTexto}${periodo}`;
}

function generarNombreRango(desde, hasta) {
  return `${convertirHora12(
    desde
  )}-${convertirHora12(hasta)}`;
}

function rangoEstaVacio(rango) {
  return (
    !rango.desde &&
    !rango.hasta &&
    Number(rango.costo) === 0 &&
    Number(rango.cupos) === 0
  );
}

function existenRangosSuperpuestos(rangos) {
  const rangosCompletos = rangos
    .filter(
      (rango) => rango.desde && rango.hasta
    )
    .map((rango) => ({
      inicio: convertirHoraAMinutos(
        rango.desde
      ),
      fin: convertirHoraAMinutos(
        rango.hasta
      ),
    }))
    .sort((a, b) => a.inicio - b.inicio);

  for (
    let indice = 1;
    indice < rangosCompletos.length;
    indice += 1
  ) {
    const anterior =
      rangosCompletos[indice - 1];

    const actual = rangosCompletos[indice];

    if (actual.inicio < anterior.fin) {
      return true;
    }
  }

  return false;
}

function convertirFormularioAJson(formulario) {
  const horarios = {};

  DIAS.forEach((dia) => {
    horarios[dia] = {};

    const rangosValidos =
      formulario.horarios[dia].filter(
        (rango) => !rangoEstaVacio(rango)
      );

    rangosValidos.forEach(
      (rango, indice) => {
        let nombreRango = generarNombreRango(
          rango.desde,
          rango.hasta
        );

        if (horarios[dia][nombreRango]) {
          nombreRango = `${nombreRango}-${
            indice + 1
          }`;
        }

        horarios[dia][nombreRango] = {
          desde: rango.desde,
          hasta: rango.hasta,
          costo: Number(
            rango.costo || 0
          ).toFixed(2),
          cupos: String(
            Number(rango.cupos || 0)
          ),
        };
      }
    );
  });

  return {
    nombre: formulario.nombre.trim(),
    tipo: formulario.tipo,
    fecha:
      formulario.tipo === "fecha"
        ? formulario.fecha
        : null,
    activo: formulario.activo,
    horarios,
  };
}

function convertirJsonAFormulario(registro) {
  const horarios = {};

  DIAS.forEach((dia) => {
    const horariosDia =
      registro.horarios?.[dia] ?? {};

    const rangos = Object.entries(
      horariosDia
    ).map(([nombreRango, valores]) => ({
      id: generarIdTemporal(),
      nombreRango,
      desde: valores.desde ?? "",
      hasta: valores.hasta ?? "",
      costo: valores.costo ?? "0.00",
      cupos: valores.cupos ?? "0",
    }));

    horarios[dia] =
      rangos.length > 0
        ? rangos
        : [crearRangoVacio()];
  });

  return {
    id: registro.id ?? null,
    nombre: registro.nombre ?? "",
    tipo: registro.tipo ?? "todos",
    fecha: registro.fecha ?? "",
    activo:
      registro.activo === true ||
      registro.activo === 1 ||
      registro.activo === "1",
    horarios,
  };
}

async function ejecutarPeticion(
  url,
  opciones = {}
) {
  const respuesta = await fetch(url, {
    ...opciones,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(opciones.headers || {}),
    },
  });

  let resultado;

  try {
    resultado = await respuesta.json();
  } catch {
    throw new Error(
      `El servidor respondió con un formato inválido. HTTP ${respuesta.status}`
    );
  }

  if (
    !respuesta.ok ||
    resultado.status === false
  ) {
    throw new Error(
      resultado.message ||
        `Error HTTP ${respuesta.status}`
    );
  }

  return resultado;
}

async function listarHorarios() {
  const resultado = await ejecutarPeticion(
    window.path_service_website+"/api/horarios-despacho"
  );

  return resultado.data || [];
}

async function obtenerHorario(id) {
  const resultado = await ejecutarPeticion(
    `${window.path_service_website+"/api/horarios-despacho"}/${id}`
  );

  return resultado.data;
}

async function crearHorario(datos) {
  const resultado = await ejecutarPeticion(
    window.path_service_website+"/api/horarios-despacho",
    {
      method: "POST",
      body: JSON.stringify(datos),
    }
  );

  return resultado.data;
}

async function actualizarHorario(id, datos) {
  const resultado = await ejecutarPeticion(
    `${window.path_service_website+"/api/horarios-despacho"}/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(datos),
    }
  );

  return resultado.data;
}

async function eliminarHorario(id) {
  const resultado = await ejecutarPeticion(
    `${window.path_service_website+"/api/horarios-despacho"}/${id}`,
    {
      method: "DELETE",
    }
  );

  return resultado.data;
}

async function actualizarEstadoHorario(
  id,
  activo
) {
  const resultado = await ejecutarPeticion(
    `${window.path_service_website+"/api/horarios-despacho"}/${id}/estado`,
    {
      method: "PATCH",
      body: JSON.stringify({
        activo,
      }),
    }
  );

  return resultado.data;
}

export default function HorariosDespacho() {
  const hoy = useMemo(
    () => obtenerFechaActual(),
    []
  );

  const [registros, setRegistros] =
    useState([]);

  const [formulario, setFormulario] =
    useState(crearFormularioVacio());

  const [modalAbierto, setModalAbierto] =
    useState(false);

  const [cargando, setCargando] =
    useState(true);

  const [guardando, setGuardando] =
    useState(false);

  const [eliminandoId, setEliminandoId] =
    useState(null);

  const [cambiandoEstadoId, setCambiandoEstadoId] =
    useState(null);

  const [errorServicio, setErrorServicio] =
    useState("");

  const cargarRegistros =
    useCallback(async () => {
      setCargando(true);
      setErrorServicio("");

      try {
        const registrosServidor =
          await listarHorarios();

        setRegistros(registrosServidor);
      } catch (error) {
        console.error(error);

        setErrorServicio(
          error.message ||
            "No se pudieron cargar los horarios."
        );
      } finally {
        setCargando(false);
      }
    }, []);

  useEffect(() => {
    cargarRegistros();
  }, [cargarRegistros]);

  function abrirNuevoHorario() {
    setFormulario(crearFormularioVacio());
    setErrorServicio("");
    setModalAbierto(true);
  }

  async function abrirEditarHorario(
    registro
  ) {
    setErrorServicio("");

    try {
      const registroCompleto =
        await obtenerHorario(registro.id);

      setFormulario(
        convertirJsonAFormulario(
          registroCompleto
        )
      );

      setModalAbierto(true);
    } catch (error) {
      console.error(error);

      alert(
        error.message ||
          "No se pudo cargar el horario."
      );
    }
  }

  function cerrarModal() {
    if (guardando) {
      return;
    }

    setModalAbierto(false);
    setFormulario(crearFormularioVacio());
  }

  function actualizarCampo(campo, valor) {
    setFormulario((estadoAnterior) => ({
      ...estadoAnterior,
      [campo]: valor,
    }));
  }

  function cambiarTipo(tipo) {
    setFormulario((estadoAnterior) => ({
      ...estadoAnterior,
      tipo,
      fecha:
        tipo === "todos"
          ? ""
          : estadoAnterior.fecha,
    }));
  }

  function actualizarRango(
    dia,
    rangoId,
    campo,
    valor
  ) {
    setFormulario((estadoAnterior) => ({
      ...estadoAnterior,
      horarios: {
        ...estadoAnterior.horarios,
        [dia]:
          estadoAnterior.horarios[dia].map(
            (rango) =>
              rango.id === rangoId
                ? {
                    ...rango,
                    [campo]: valor,
                  }
                : rango
          ),
      },
    }));
  }

  function agregarRango(dia) {
    setFormulario((estadoAnterior) => ({
      ...estadoAnterior,
      horarios: {
        ...estadoAnterior.horarios,
        [dia]: [
          ...estadoAnterior.horarios[dia],
          crearRangoVacio(),
        ],
      },
    }));
  }

  function eliminarRango(
    dia,
    rangoId
  ) {
    setFormulario((estadoAnterior) => {
      const rangosActuales =
        estadoAnterior.horarios[dia];

      if (rangosActuales.length === 1) {
        return {
          ...estadoAnterior,
          horarios: {
            ...estadoAnterior.horarios,
            [dia]: [crearRangoVacio()],
          },
        };
      }

      return {
        ...estadoAnterior,
        horarios: {
          ...estadoAnterior.horarios,
          [dia]: rangosActuales.filter(
            (rango) =>
              rango.id !== rangoId
          ),
        },
      };
    });
  }

  function limpiarDia(dia) {
    setFormulario((estadoAnterior) => ({
      ...estadoAnterior,
      horarios: {
        ...estadoAnterior.horarios,
        [dia]: [crearRangoVacio()],
      },
    }));
  }

  function copiarATodos(diaOrigen) {
    const rangosOrigen =
      formulario.horarios[diaOrigen];

    setFormulario((estadoAnterior) => {
      const nuevosHorarios = {};

      DIAS.forEach((dia) => {
        nuevosHorarios[dia] =
          rangosOrigen.map((rango) => ({
            ...rango,
            id: generarIdTemporal(),
          }));
      });

      return {
        ...estadoAnterior,
        horarios: nuevosHorarios,
      };
    });
  }

  function validarFormulario() {
    if (!formulario.nombre.trim()) {
      alert(
        "Ingrese un nombre para el horario."
      );
      return false;
    }

    if (
      formulario.tipo === "fecha" &&
      !formulario.fecha
    ) {
      alert(
        "Seleccione una fecha específica."
      );
      return false;
    }

    if (
      formulario.tipo === "fecha" &&
      formulario.fecha < hoy
    ) {
      alert(
        "La fecha no puede ser menor al día actual."
      );
      return false;
    }

    let tieneHorarios = false;

    for (const dia of DIAS) {
      const rangos =
        formulario.horarios[dia];

      for (
        let indice = 0;
        indice < rangos.length;
        indice += 1
      ) {
        const rango = rangos[indice];

        if (rangoEstaVacio(rango)) {
          continue;
        }

        tieneHorarios = true;

        if (
          !rango.desde ||
          !rango.hasta
        ) {
          alert(
            `Complete Desde y Hasta en ${dia}, fila ${
              indice + 1
            }.`
          );
          return false;
        }

        if (
          convertirHoraAMinutos(
            rango.desde
          ) >=
          convertirHoraAMinutos(
            rango.hasta
          )
        ) {
          alert(
            `La hora Hasta debe ser mayor que Desde en ${dia}, fila ${
              indice + 1
            }.`
          );
          return false;
        }

        if (
          rango.costo === "" ||
          Number(rango.costo) < 0
        ) {
          alert(
            `Ingrese un costo válido en ${dia}, fila ${
              indice + 1
            }.`
          );
          return false;
        }

        if (
          rango.cupos === "" ||
          !Number.isInteger(
            Number(rango.cupos)
          ) ||
          Number(rango.cupos) < 0
        ) {
          alert(
            `Ingrese cupos válidos en ${dia}, fila ${
              indice + 1
            }.`
          );
          return false;
        }
      }

      if (
        existenRangosSuperpuestos(rangos)
      ) {
        alert(
          `Existen horarios superpuestos en ${dia}.`
        );
        return false;
      }
    }

    if (!tieneHorarios) {
      alert(
        "Debe registrar al menos un horario."
      );
      return false;
    }

    return true;
  }

  async function guardarHorario() {
    if (
      !validarFormulario() ||
      guardando
    ) {
      return;
    }

    setGuardando(true);
    setErrorServicio("");

    try {
      const datos =
        convertirFormularioAJson(
          formulario
        );

      let registroGuardado;

      if (formulario.id !== null) {
        registroGuardado =
          await actualizarHorario(
            formulario.id,
            datos
          );

        setRegistros(
          (registrosAnteriores) =>
            registrosAnteriores.map(
              (registro) =>
                registro.id ===
                registroGuardado.id
                  ? registroGuardado
                  : registro
            )
        );
      } else {
        registroGuardado =
          await crearHorario(datos);

        setRegistros(
          (registrosAnteriores) => [
            registroGuardado,
            ...registrosAnteriores,
          ]
        );
      }

      alert(
        formulario.id !== null
          ? "Horario actualizado correctamente."
          : "Horario creado correctamente."
      );

      setModalAbierto(false);
      setFormulario(
        crearFormularioVacio()
      );
    } catch (error) {
      console.error(error);

      setErrorServicio(
        error.message ||
          "No se pudo guardar el horario."
      );

      alert(
        error.message ||
          "No se pudo guardar el horario."
      );
    } finally {
      setGuardando(false);
    }
  }

  async function eliminarRegistro(id) {
    const confirmado = window.confirm(
      "¿Está seguro de eliminar este horario?"
    );

    if (!confirmado) {
      return;
    }

    setEliminandoId(id);
    setErrorServicio("");

    try {
      await eliminarHorario(id);

      setRegistros(
        (registrosAnteriores) =>
          registrosAnteriores.filter(
            (registro) =>
              registro.id !== id
          )
      );

      alert(
        "Horario eliminado correctamente."
      );
    } catch (error) {
      console.error(error);

      setErrorServicio(
        error.message ||
          "No se pudo eliminar el horario."
      );

      alert(
        error.message ||
          "No se pudo eliminar el horario."
      );
    } finally {
      setEliminandoId(null);
    }
  }

  async function cambiarEstado(id) {
    const registroActual =
      registros.find(
        (registro) =>
          registro.id === id
      );

    if (!registroActual) {
      return;
    }

    setCambiandoEstadoId(id);
    setErrorServicio("");

    try {
      const registroActualizado =
        await actualizarEstadoHorario(
          id,
          !registroActual.activo
        );

      setRegistros(
        (registrosAnteriores) =>
          registrosAnteriores.map(
            (registro) =>
              registro.id === id
                ? registroActualizado
                : registro
          )
      );
    } catch (error) {
      console.error(error);

      setErrorServicio(
        error.message ||
          "No se pudo cambiar el estado."
      );

      alert(
        error.message ||
          "No se pudo cambiar el estado."
      );
    } finally {
      setCambiandoEstadoId(null);
    }
  }

  return (
    <div className="pagina-horarios">
      <header className="cabecera-principal">
        <div>
          <h1>
            Horarios de despacho
          </h1>

          <p>
            Administra los días, rangos,
            costos y cupos.
          </p>
        </div>

        <button
          type="button"
          className="btn-nuevo"
          onClick={abrirNuevoHorario}
        >
          + Crear horario
        </button>
      </header>

      {errorServicio && (
        <div className="mensaje-error">
          <span>{errorServicio}</span>

          <button
            type="button"
            onClick={cargarRegistros}
          >
            Reintentar
          </button>
        </div>
      )}

      {cargando ? (
        <div className="mensaje-cargando">
          Cargando horarios...
        </div>
      ) : (
        <ListadoHorarios
          registros={registros}
          eliminandoId={eliminandoId}
          cambiandoEstadoId={
            cambiandoEstadoId
          }
          onEditar={
            abrirEditarHorario
          }
          onEliminar={
            eliminarRegistro
          }
          onCambiarEstado={
            cambiarEstado
          }
        />
      )}

      {modalAbierto && (
        <ModalHorario
          formulario={formulario}
          hoy={hoy}
          guardando={guardando}
          onCerrar={cerrarModal}
          onGuardar={guardarHorario}
          onActualizarCampo={
            actualizarCampo
          }
          onCambiarTipo={
            cambiarTipo
          }
          onActualizarRango={
            actualizarRango
          }
          onAgregarRango={
            agregarRango
          }
          onEliminarRango={
            eliminarRango
          }
          onLimpiarDia={limpiarDia}
          onCopiarATodos={
            copiarATodos
          }
        />
      )}
    </div>
  );
}

function ListadoHorarios({
  registros,
  eliminandoId,
  cambiandoEstadoId,
  onEditar,
  onEliminar,
  onCambiarEstado,
}) {
  function contarRangos(registro) {
    return DIAS.reduce(
      (total, dia) =>
        total +
        Object.keys(
          registro.horarios?.[dia] ?? {}
        ).length,
      0
    );
  }

  return (
    <div className="listado-contenedor">
      <table className="tabla-listado">
        <thead>
          <tr>
            <th>ID</th>
            <th>Nombre</th>
            <th>Tipo</th>
            <th>Fecha</th>
            <th>Rangos</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>

        <tbody>
          {registros.length === 0 ? (
            <tr>
              <td
                colSpan={7}
                className="sin-registros"
              >
                No existen horarios
                registrados.
              </td>
            </tr>
          ) : (
            registros.map((registro) => (
              <tr key={registro.id}>
                <td>{registro.id}</td>

                <td>
                  <strong>
                    {registro.nombre}
                  </strong>
                </td>

                <td>
                  {registro.tipo === "todos"
                    ? "Todos los días"
                    : "Fecha específica"}
                </td>

                <td>
                  {registro.fecha || "-"}
                </td>

                <td>
                  {contarRangos(
                    registro
                  )}
                </td>

                <td>
                  <button
                    type="button"
                    disabled={
                      cambiandoEstadoId ===
                      registro.id
                    }
                    className={
                      registro.activo
                        ? "estado estado-activo"
                        : "estado estado-inactivo"
                    }
                    onClick={() =>
                      onCambiarEstado(
                        registro.id
                      )
                    }
                  >
                    {cambiandoEstadoId ===
                    registro.id
                      ? "CAMBIANDO..."
                      : registro.activo
                        ? "ACTIVO"
                        : "INACTIVO"}
                  </button>
                </td>

                <td>
                  <div className="acciones-listado">
                    <button
                      type="button"
                      className="btn-editar"
                      disabled={
                        eliminandoId ===
                        registro.id
                      }
                      onClick={() =>
                        onEditar(registro)
                      }
                    >
                      Editar
                    </button>

                    <button
                      type="button"
                      className="btn-eliminar"
                      disabled={
                        eliminandoId ===
                        registro.id
                      }
                      onClick={() =>
                        onEliminar(
                          registro.id
                        )
                      }
                    >
                      {eliminandoId ===
                      registro.id
                        ? "Eliminando..."
                        : "Eliminar"}
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function ModalHorario({
  formulario,
  hoy,
  guardando,
  onCerrar,
  onGuardar,
  onActualizarCampo,
  onCambiarTipo,
  onActualizarRango,
  onAgregarRango,
  onEliminarRango,
  onLimpiarDia,
  onCopiarATodos,
}) {
  useEffect(() => {
    const overflowAnterior =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function cerrarConEscape(evento) {
      if (
        evento.key === "Escape" &&
        !guardando
      ) {
        onCerrar();
      }
    }

    document.addEventListener(
      "keydown",
      cerrarConEscape
    );

    return () => {
      document.body.style.overflow =
        overflowAnterior;

      document.removeEventListener(
        "keydown",
        cerrarConEscape
      );
    };
  }, [guardando, onCerrar]);

  const contenidoModal = (
    <div
      className="modal-overlay"
      onMouseDown={(evento) => {
        if (
          evento.target ===
            evento.currentTarget &&
          !guardando
        ) {
          onCerrar();
        }
      }}
    >
      <div
        className="modal-horario"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tituloModalHorario"
        onMouseDown={(evento) =>
          evento.stopPropagation()
        }
      >
        <div className="modal-cabecera">
          <div>
            <h2 id="tituloModalHorario">
              {formulario.id !== null
                ? "Editar horario de despacho"
                : "Crear horario de despacho"}
            </h2>

            <span>
              {formulario.id !== null
                ? `Registro N.° ${formulario.id}`
                : "Nuevo registro"}
            </span>
          </div>

          <button
            type="button"
            className="btn-cerrar-modal"
            disabled={guardando}
            aria-label="Cerrar modal"
            onClick={onCerrar}
          >
            ×
          </button>
        </div>

        <div className="modal-contenido">
          <div className="configuracion-general">
            <div className="campo campo-nombre">
              <label htmlFor="nombreHorario">
                Nombre
              </label>

              <input
                id="nombreHorario"
                type="text"
                maxLength={150}
                value={formulario.nombre}
                placeholder="Ejemplo: Horario general"
                onChange={(evento) =>
                  onActualizarCampo(
                    "nombre",
                    evento.target.value
                  )
                }
              />
            </div>

            <div className="campo">
              <label htmlFor="tipoHorario">
                Tipo
              </label>

              <select
                id="tipoHorario"
                value={formulario.tipo}
                onChange={(evento) =>
                  onCambiarTipo(
                    evento.target.value
                  )
                }
              >
                <option value="todos">
                  Todos los días
                </option>

                <option value="fecha">
                  Fecha específica
                </option>
              </select>
            </div>

            {formulario.tipo ===
              "fecha" && (
              <div className="campo">
                <label htmlFor="fechaHorario">
                  Fecha específica
                </label>

                <input
                  id="fechaHorario"
                  type="date"
                  min={hoy}
                  value={
                    formulario.fecha
                  }
                  onChange={(evento) =>
                    onActualizarCampo(
                      "fecha",
                      evento.target.value
                    )
                  }
                />
              </div>
            )}

            <div className="campo campo-estado">
              <label>Estado</label>

              <label className="switch-contenedor">
                <input
                  type="checkbox"
                  checked={
                    formulario.activo
                  }
                  onChange={(evento) =>
                    onActualizarCampo(
                      "activo",
                      evento.target.checked
                    )
                  }
                />

                <span className="switch" />

                <span>
                  {formulario.activo
                    ? "Activo"
                    : "Inactivo"}
                </span>
              </label>
            </div>
          </div>

          <div className="titulo-horarios">
            <h3>
              Horarios de visualización
            </h3>

            <p>
              Puede agregar varios rangos
              por cada día.
            </p>
          </div>

          <div className="dias-contenedor">
            {DIAS.map((dia) => (
              <SeccionDia
                key={dia}
                dia={dia}
                rangos={
                  formulario.horarios[dia]
                }
                onActualizarRango={
                  onActualizarRango
                }
                onAgregarRango={
                  onAgregarRango
                }
                onEliminarRango={
                  onEliminarRango
                }
                onLimpiarDia={
                  onLimpiarDia
                }
                onCopiarATodos={
                  onCopiarATodos
                }
              />
            ))}
          </div>
        </div>

        <div className="modal-pie">
          <button
            type="button"
            className="btn-cancelar"
            disabled={guardando}
            onClick={onCerrar}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="btn-guardar"
            disabled={guardando}
            onClick={onGuardar}
          >
            {guardando
              ? "Guardando..."
              : formulario.id !== null
                ? "Guardar cambios"
                : "Crear horario"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    contenidoModal,
    document.body
  );
}

function SeccionDia({
  dia,
  rangos,
  onActualizarRango,
  onAgregarRango,
  onEliminarRango,
  onLimpiarDia,
  onCopiarATodos,
}) {
  return (
    <section className="dia-bloque">
      <div className="dia-cabecera">
        <h4>{dia}</h4>

        <div className="acciones-dia">
          <button
            type="button"
            className="btn-copiar"
            onClick={() =>
              onCopiarATodos(dia)
            }
          >
            Copiar a todos
          </button>

          <button
            type="button"
            className="btn-limpiar"
            onClick={() =>
              onLimpiarDia(dia)
            }
          >
            Limpiar
          </button>

          <button
            type="button"
            className="btn-agregar-rango"
            onClick={() =>
              onAgregarRango(dia)
            }
          >
            + Agregar rango
          </button>
        </div>
      </div>

      <div className="tabla-dia-wrapper">
        <table className="tabla-dia">
          <thead>
            <tr>
              <th>Desde</th>
              <th>Hasta</th>
              <th>Costo</th>
              <th>Cupos</th>
              <th>Acción</th>
            </tr>
          </thead>

          <tbody>
            {rangos.map(
              (rango, indice) => (
                <tr key={rango.id}>
                  <td>
                    <input
                      type="time"
                      value={
                        rango.desde
                      }
                      onChange={(
                        evento
                      ) =>
                        onActualizarRango(
                          dia,
                          rango.id,
                          "desde",
                          evento.target
                            .value
                        )
                      }
                    />
                  </td>

                  <td>
                    <input
                      type="time"
                      value={
                        rango.hasta
                      }
                      onChange={(
                        evento
                      ) =>
                        onActualizarRango(
                          dia,
                          rango.id,
                          "hasta",
                          evento.target
                            .value
                        )
                      }
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        rango.costo
                      }
                      onChange={(
                        evento
                      ) =>
                        onActualizarRango(
                          dia,
                          rango.id,
                          "costo",
                          evento.target
                            .value
                        )
                      }
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={
                        rango.cupos
                      }
                      onChange={(
                        evento
                      ) =>
                        onActualizarRango(
                          dia,
                          rango.id,
                          "cupos",
                          evento.target
                            .value
                        )
                      }
                    />
                  </td>

                  <td>
                    <button
                      type="button"
                      className="btn-eliminar-rango"
                      onClick={() =>
                        onEliminarRango(
                          dia,
                          rango.id
                        )
                      }
                    >
                      {rangos.length === 1
                        ? "Vaciar"
                        : `Eliminar ${
                            indice + 1
                          }`}
                    </button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}